import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Container, Card, CardBody, Row, Col, FormGroup, Label, Input,
    Button, Alert, Spinner, Table, Modal, ModalHeader, ModalBody, Collapse, Badge
} from "reactstrap";
import { Typeahead } from 'react-bootstrap-typeahead';
import AuthFunctions from '../../services/AuthService';
import Session from '../../auth/Session';
import { useNavigate } from 'react-router-dom';
import * as dayjs from 'dayjs';
import PaySheetAdjustmentForm from './PaySheetAdjustmentForm';
import { getFinanceFilters, saveFinanceFilter } from "./financeFilterStorage";
import { WT_ADT_ENABLED } from "../../../featureFlags";

const isOvertimeRow = (detail) => detail?.detailType === 'Overtime';
// A flat "False Trip" driver-pay line, identified by its detailType.
const isFalseTripRow = (detail) => detail?.detailType === 'FalseTrip';
// A "Backup Driver" standby line, identified by its detailType.
const isBackupDriverRow = (detail) => detail?.detailType === 'BackupDriver';
// A backup "make-up" line (base − route pay for short coverage days).
const isBackupMakeupRow = (detail) => detail?.detailType === 'BackupMakeup';
// A "Lead Pay" management line, identified by its detailType.
const isLeadDriverRow = (detail) => detail?.detailType === 'LeadDriver';
// A manual one-off "Adjustment" pay line (description + qty + amount + transaction type).
const isAdjustmentRow = (detail) => detail?.detailType === 'Adjustment';
// A comment-only line (description, no amount). Comes back with detailType "Comment".
const isCommentRow = (detail) => detail?.detailType === 'Comment';
const isSurchargeRow = (detail) => detail?.detailType === 'Surcharge' || detail?.surchargeId != null || detail?.surchargeTypeId != null;
const isBaseRouteRow = (detail) => detail?.detailType === 'Route'
    || (detail?.surchargeId == null && detail?.surchargeTypeId == null && !isOvertimeRow(detail) && !isFalseTripRow(detail) && !isBackupDriverRow(detail) && !isBackupMakeupRow(detail) && !isLeadDriverRow(detail) && !isAdjustmentRow(detail) && !isCommentRow(detail));
const isDriverSurchargeRow = (detail) => detail?.surchargeTargetType === 'Driver';
const isPercentageSurchargeRow = (detail) => Number(detail?.surchargeTypeId ?? 0) === 2
    || `${detail?.surchargeTypeName ?? ""}`.toLowerCase() === "percentage";
const isFuelSurchargeRow = (detail) => Number(detail?.surchargeTypeId ?? 0) === 5
    || `${detail?.surchargeTypeName ?? ""}`.toLowerCase().includes("fuel");
// Mirrors OvertimeCalculator.cs: Math.Floor(rawOt / 0.5) * 0.5.
// Uses the route config from the parent paysheet detail row.
// Round a monetary value to 2 decimal places the same way money() displays it,
// so that summed displayed line items always equal the displayed total.
const roundMoney = (val) => Math.round((Number(val ?? 0) + Number.EPSILON) * 100) / 100;

const calcDailyOt = (startTime, endTime, route) => {
    if (!route?.allowOvertime) return "";
    const cutoff = route.billingType === "Half-day"
        ? Number(route.halfDayOT ?? 0)
        : Number(route.fullDayOT ?? 0);
    if (cutoff <= 0) return "";
    const toHours = (t) => {
        if (!t) return null;
        const [h, m] = t.split(':').map(Number);
        if (isNaN(h) || isNaN(m)) return null;
        return h + m / 60;
    };
    const s = toHours(startTime);
    let e = toHours(endTime);
    if (s === null || e === null) return "";
    // An end time earlier than the start time means the shift ran past midnight into the
    // next day (e.g. PM start, AM end); roll the end forward 24h. Mirrors the backend
    // OvertimeCalculator.GetWorkedHours.
    if (e < s) e += 24;
    const total = e - s;
    if (total <= cutoff) return "";
    const rawOt = total - cutoff;
    if (route.roundOvertimeToHalfHour) {
        const increments = Math.round(Math.floor(rawOt / 0.5) * 0.5 * 2);
        return increments > 0 ? `${increments} × ½hr` : "";
    }
    return rawOt.toFixed(2);
};

const rowQuantity = (detail, isBaseRow, isOvertime, hoursFormatter) => {
    if (isBaseRow) {
        // Show the quantity at 2 decimals so hourly worked-hours read cleanly (7.62, not
        // 7.616666…), matching the Hours column and the invoice's lineQuantity formatter.
        return detail.billingType === "Hourly"
            ? `${hoursFormatter(detail.daysOnRoute)} hrs`
            : hoursFormatter(detail.daysOnRoute);
    }
    if (isOvertime) {
        if (detail?.roundOvertimeToHalfHour === true) {
            const increments = Math.round(Math.floor(Number(detail.daysOnRoute ?? 0) / 0.5) * 0.5 * 2);
            return increments > 0 ? `${increments} × ½hr` : hoursFormatter(detail.daysOnRoute);
        }
        return hoursFormatter(detail.daysOnRoute);
    }
    if (isPercentageSurchargeRow(detail)) return `${Number(detail?.surchargePercentage ?? 0).toFixed(2)}%`;
    return hoursFormatter(detail.daysOnRoute);
};
const getRouteId = (detail) => detail?.routeId ?? null;
const getRouteInfo = (detail, routeLookup) => {
    const routeId = getRouteId(detail);
    const lookupInfo = routeId != null ? routeLookup?.get(routeId) : null;
    return {
        id: routeId,
        location: detail?.location || lookupInfo?.location || '',
        routeName: detail?.routeName || lookupInfo?.routeName || ''
    };
};
const getRouteKey = (detail, routeLookup) => {
    const info = getRouteInfo(detail, routeLookup);
    return `${info.location}|${info.routeName}|${info.id ?? ''}`;
};
const getDisplayRows = (rows, routeLookup) => {
    const baseRouteRowIds = new Set();
    const firstRouteRowIds = new Set();
    let firstDriverRowId = null;

    for (const row of rows) {
        if (!isBaseRouteRow(row) || getRouteId(row) == null) {
            continue;
        }

        baseRouteRowIds.add(row.id);
        const routeKey = getRouteKey(row, routeLookup);
        if (!firstRouteRowIds.has(routeKey)) {
            firstRouteRowIds.add(routeKey);
        }

        if (firstDriverRowId == null) {
            firstDriverRowId = row.id;
        }
    }

    if (firstDriverRowId == null && rows.length > 0) {
        firstDriverRowId = rows[0].id;
    }

    return rows.map(row => ({
        detail: row,
        showDriverName: row.id === firstDriverRowId,
        showRouteInfo: baseRouteRowIds.has(row.id) && firstRouteRowIds.has(getRouteKey(row, routeLookup))
    }));
};
const getPaySheetDrivers = (data) => Array.isArray(data) ? data : (data?.drivers || []);
const getPaySheetDetails = (paySheet) => {
    const routeDetails = (paySheet?.routes || []).flatMap(route => {
        const routeRows = route?.details || [];
        const baseRows = routeRows.filter(isBaseRouteRow);
        const otherRows = routeRows.filter(row => !isBaseRouteRow(row));
        return [...baseRows, ...otherRows];
    });

    return [...routeDetails, ...(paySheet?.details || [])];
};

const PaySheetReview = () => {
    const navigate = useNavigate();
    const savedFilters = getFinanceFilters();
    const generateApi = (driverId, endingDate) => `/api/paysheets/generate?driverId=${driverId || 0}&endingDate=${endingDate}`;
    const routeWeeksApi = "/api/routes/invoice-weeks";
    const deleteSurchargeApi = (paySheetId, paySheetDetailId) => `/api/paysheets/${paySheetId}/delete-detail/${paySheetDetailId}`;
    const submitApi = (paySheetId) => `/api/paysheets/${paySheetId}/submit/true`;
    const dailyBreakdownApi = (paySheetId, detailId) => `/api/paysheets/${paySheetId}/detail/${detailId}/daily-breakdown`;
    const voidApi = (paySheetId) => `/api/paysheets/${paySheetId}/void`;

    const [drivers, setDrivers] = useState([]);
    const [driverId, setDriverId] = useState("");
    const [loading, setLoading] = useState(false);
    const [schedule, setSchedule] = useState(null);
    const [paySheetDrivers, setPaySheetDrivers] = useState([]);
    const [error, setError] = useState("");
    const [weeks, setWeeks] = useState([{ endingDate: '', displayDate: '' }]);
    const [selectedWeek, setSelectedWeek] = useState(savedFilters.selectedWeek || '');
    const [adjustmentOpen, setAdjustmentOpen] = useState(false);
    const [adjustmentEditing, setAdjustmentEditing] = useState(null);
    const [adjustmentPaySheetId, setAdjustmentPaySheetId] = useState(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [submitOpen, setSubmitOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [notesOpen, setNotesOpen] = useState(false);
    const [notesText, setNotesText] = useState("");
    const [editRatePaySheets, setEditRatePaySheets] = useState(new Set());
    const [openRows, setOpenRows] = useState(new Set());
    const [detailsCache, setDetailsCache] = useState({});
    const [loadingDetails, setLoadingDetails] = useState({});
    const [actionLoading, setActionLoading] = useState(false);

    // Void ("false paycheck") modal state.
    const [voidOpen, setVoidOpen] = useState(false);
    const [voidPaySheetId, setVoidPaySheetId] = useState(null);
    const [voidReason, setVoidReason] = useState("");
    const [voidSubmitting, setVoidSubmitting] = useState(false);

    // Add standalone ("training only") paysheet modal state.
    const emptyAddForm = {
        driverId: "",
        endingDate: "",
        description: "",
        amount: "",
        quantity: "1",
        transactionTypeId: "",
        isComment: false,
    };
    const [addOpen, setAddOpen] = useState(false);
    const [addForm, setAddForm] = useState(emptyAddForm);
    const [addErrors, setAddErrors] = useState({});
    const [addSubmitting, setAddSubmitting] = useState(false);
    const [allDrivers, setAllDrivers] = useState([]);
    const [addTransactionTypes, setAddTransactionTypes] = useState([]);
    // Active sub-customers, used by the inline Class picker on route-less adjustment / driver-surcharge
    // lines (a line's QBO Class is its sub-customer).
    const [subCustomers, setSubCustomers] = useState([]);

    // "Change Route" modal: reassign a route line to another route (e.g. its "No Delivery" sibling)
    // without void + reimport.
    const [changeRouteOpen, setChangeRouteOpen] = useState(false);
    const [changeRouteDetail, setChangeRouteDetail] = useState(null);
    const [changeRouteOptions, setChangeRouteOptions] = useState([]);
    const [changeRouteValue, setChangeRouteValue] = useState("");
    const [changeRouteLoading, setChangeRouteLoading] = useState(false);
    const [changeRouteSubmitting, setChangeRouteSubmitting] = useState(false);

    useEffect(() => {
        const loadData = async () => {
            const token = await Session.getToken();
            fetch(routeWeeksApi, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).then(setWeeks).catch(() => setWeeks([]));
        };
        loadData();
    }, []);

    // Load the full driver list and transaction types once, for the Add-Paysheet picker/first line.
    useEffect(() => {
        const load = async () => {
            const token = await Session.getToken();
            fetch("/api/drivers/list", { headers: { Authorization: `Bearer ${token}` } })
                .then(r => r.ok ? r.json() : []).then(setAllDrivers).catch(() => setAllDrivers([]));
            fetch("/api/paysheets/transaction-types", { headers: { Authorization: `Bearer ${token}` } })
                .then(r => r.ok ? r.json() : []).then(setAddTransactionTypes).catch(() => setAddTransactionTypes([]));
            fetch("/api/SubCustomers", { headers: { Authorization: `Bearer ${token}` } })
                .then(r => r.ok ? r.json() : [])
                .then(list => setSubCustomers((list || []).filter(s => s.isActive).sort((a, b) => (a.name || '').localeCompare(b.name || ''))))
                .catch(() => setSubCustomers([]));
        };
        load();
    }, []);

    useEffect(() => {
        const filters = getFinanceFilters();
        const selected = filters.selectedWeek
            ? weeks.find(w => w.endingDate === filters.selectedWeek)
            : weeks.find(w => w.selected === true);
        if (selected) {
            setSelectedWeek(selected.endingDate);
        }
    }, [weeks]);

    const loadPaySheetData = useCallback(async ({ showLoading = true } = {}) => {
        const isAuthenticated = await AuthFunctions.getAuthenticatedAsync();
        if (!isAuthenticated) {
            navigate('/logout');
            return;
        }

        if (!selectedWeek) return;

        if (showLoading) setLoading(true);
        setError("");

        try {
            const token = await Session.getToken();
            const payWeek = selectedWeek ? dayjs(selectedWeek, 'MM/DD/YYYY').format('YYYY-MM-DD') : dayjs().subtract(7, 'day').format('YYYY-MM-DD');

            const [driversResponse, res] = await Promise.all([
                fetch(`/api/paysheets/drivers?endingDate=${payWeek}`, { headers: { Authorization: `Bearer ${token}` } }),
                fetch(`/api/paysheets/for-week?endingDate=${payWeek}&type=review`, { headers: { Authorization: `Bearer ${token}` } })
            ]);
            const driverData = driversResponse.ok ? await driversResponse.json() : [];
            setDrivers(driverData);

            if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);

            const data = await res.json();
            setSchedule(data.schedule);
            setPaySheetDrivers(getPaySheetDrivers(data));
            setOpenRows(new Set());
            setDetailsCache({});

            setDriverId(currentDriverId =>
                currentDriverId && !driverData.some(d => `${d.id}` === `${currentDriverId}`)
                    ? ''
                    : currentDriverId
            );
        } catch (e) {
            setError(e.message || 'Failed to load pay sheets.');
        } finally {
            if (showLoading) setLoading(false);
        }
    }, [navigate, selectedWeek]);

    useEffect(() => {
        loadPaySheetData();
    }, [loadPaySheetData]);

    const fmt = (d) => (d ? new Date(d).toLocaleDateString() : "");
    const money = (n) => (n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    // Fuel-surcharge rates are per-mile and need 3 decimals to read accurately (e.g. 0.119).
    const rateMoney = (detail) => {
        const digits = isFuelSurchargeRow(detail) ? 3 : 2;
        return (detail?.payRate ?? 0).toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
    };
    const hours = (n) => (Number(n || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    // Persist an edited guarantee-days value on a route-less backup or lead line, then refresh so the
    // recomputed standby/make-up/lead totals show. Mirrors the ADT override edit pattern.
    const saveGuaranteeDays = async (detail, value) => {
        const num = value === "" ? 0 : Number(value);
        if (isNaN(num) || num < 0) return;
        // Backup lines edit the standby days paid (daysOnRoute); lead lines edit guaranteed days.
        const current = isBackupDriverRow(detail)
            ? Number(detail.daysOnRoute ?? 0)
            : Number(detail.guaranteeDays ?? 5);
        if (num === current) return;
        try {
            const token = await Session.getToken();
            const res = await fetch('/api/paysheets/update-guarantee-days', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ paySheetId: detail.paySheetId, paySheetDetailId: detail.id, guaranteeDays: num })
            });
            if (!res.ok) throw new Error(await res.text());
            await loadPaySheetData({ showLoading: false });
        } catch (e) {
            setError(e.message || 'Failed to update guarantee days.');
        }
    };

    // Toggle per-paysheet "edit rates" mode. Kept in its own Set (not reset on data reload) so the
    // mode stays on across the post-save refresh.
    const toggleEditRate = (paySheetId) => {
        setEditRatePaySheets(prev => {
            const next = new Set(prev);
            if (next.has(paySheetId)) next.delete(paySheetId); else next.add(paySheetId);
            return next;
        });
    };

    // Persist (or clear) a temporary per-line pay-rate override on a route/overtime line, then apply
    // the recomputed totals. An empty value clears the override, reverting to the route's rate.
    const saveRate = async (detail, value) => {
        const trimmed = `${value}`.trim();
        const payRate = trimmed === "" ? null : Number(trimmed);
        if (payRate !== null && (isNaN(payRate) || payRate < 0)) return;
        // No-op when nothing actually changes.
        if (payRate === null && detail.payRateOverride == null) return;
        if (payRate !== null && detail.payRateOverride == null && payRate === Number(detail.payRate ?? 0)) return;
        if (payRate !== null && detail.payRateOverride != null && payRate === Number(detail.payRateOverride)) return;
        try {
            const token = await Session.getToken();
            const res = await fetch('/api/paysheets/update-rate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ paySheetId: detail.paySheetId, paySheetDetailId: detail.id, payRate })
            });
            if (!res.ok) throw new Error(await res.text());
            const data = await res.json();
            applyPaySheetDriverUpdate(data);
        } catch (e) {
            setError(e.message || 'Failed to update rate.');
        }
    };

    // Persist (or clear) a per-line override of days-on-route / hours / mileage on a base route line,
    // then apply the recomputed totals. An empty value sends null, clearing the override (reverting to
    // the schedule-derived value). Mirrors saveRate's post-and-replace pattern.
    const saveRouteFieldOverride = async (url, detail, value, overrideKey, baseKey) => {
        const trimmed = `${value}`.trim();
        const num = trimmed === "" ? null : Number(trimmed);
        if (num !== null && (isNaN(num) || num < 0)) return;
        // No-op when nothing actually changes.
        if (num === null && detail[overrideKey] == null) return;
        if (num !== null && detail[overrideKey] == null && num === Number(detail[baseKey] ?? 0)) return;
        if (num !== null && detail[overrideKey] != null && num === Number(detail[overrideKey])) return;
        try {
            const token = await Session.getToken();
            const body = { paySheetId: detail.paySheetId, paySheetDetailId: detail.id };
            if (url.endsWith('update-days-on-route')) body.daysOnRoute = num;
            else if (url.endsWith('update-hours')) body.totalHoursWorked = num;
            else body.totalMileage = num;
            const res = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify(body)
            });
            if (!res.ok) throw new Error(await res.text());
            const data = await res.json();
            applyPaySheetDriverUpdate(data);
        } catch (e) {
            setError(e.message || 'Failed to update value.');
        }
    };
    // Persist the QBO Class (= sub-customer) chosen for a route-less adjustment / driver-surcharge line,
    // then apply the recomputed data. An empty value clears the assignment.
    const saveLineSubCustomer = async (detail, value) => {
        const subCustomerId = value === "" ? null : Number(value);
        if ((detail.assignedSubCustomerId ?? null) === (subCustomerId ?? null)) return;
        try {
            const token = await Session.getToken();
            const res = await fetch('/api/paysheets/update-line-subcustomer', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ paySheetId: detail.paySheetId, paySheetDetailId: detail.id, subCustomerId })
            });
            if (!res.ok) throw new Error(await res.text());
            const data = await res.json();
            applyPaySheetDriverUpdate(data);
        } catch (e) {
            setError(e.message || 'Failed to update class.');
        }
    };

    // Persist (or clear) a manual line-total override on any dollar-carrying line, then apply the
    // recomputed totals. An empty value clears the override, reverting the line to its computed total.
    const saveLineTotal = async (detail, value) => {
        const trimmed = `${value}`.trim();
        const routeTotal = trimmed === "" ? null : Number(trimmed);
        if (routeTotal !== null && isNaN(routeTotal)) return;
        // No-op when nothing actually changes.
        if (routeTotal === null && detail.routeTotalOverride == null) return;
        if (routeTotal !== null && detail.routeTotalOverride == null && routeTotal === Number(detail.routeTotal ?? 0)) return;
        if (routeTotal !== null && detail.routeTotalOverride != null && routeTotal === Number(detail.routeTotalOverride)) return;
        try {
            const token = await Session.getToken();
            const res = await fetch('/api/paysheets/update-line-total', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ paySheetId: detail.paySheetId, paySheetDetailId: detail.id, routeTotal })
            });
            if (!res.ok) throw new Error(await res.text());
            const data = await res.json();
            applyPaySheetDriverUpdate(data);
        } catch (e) {
            setError(e.message || 'Failed to update line total.');
        }
    };

    const saveDaysOnRoute = (detail, value) => saveRouteFieldOverride('/api/paysheets/update-days-on-route', detail, value, 'daysOnRouteOverride', 'daysOnRoute');
    const saveHours = (detail, value) => saveRouteFieldOverride('/api/paysheets/update-hours', detail, value, 'totalHoursWorkedOverride', 'totalHoursWorked');
    const saveMileage = (detail, value) => saveRouteFieldOverride('/api/paysheets/update-mileage', detail, value, 'totalMileageOverride', 'totalMileage');

    const formatAmPm = (time) => {
        if (!time) return "";
        const [h, m] = time.split(':').map(Number);
        if (isNaN(h) || isNaN(m)) return time;
        const period = h < 12 ? "AM" : "PM";
        const hour = h % 12 || 12;
        return `${hour}:${String(m).padStart(2, '0')} ${period}`;
    };

    const filteredPaySheetDrivers = useMemo(() => {
        if (!driverId) return [...paySheetDrivers];
        return paySheetDrivers.filter(d => `${d?.driverId}` === `${driverId}`);
    }, [paySheetDrivers, driverId]);

    const groupedPaySheets = useMemo(() => {
        return filteredPaySheetDrivers.flatMap(driver => (driver.paysheets || []).map(paySheet => ({
            paySheetId: paySheet.paySheetId,
            paySheet,
            driverId: driver.driverId,
            driverName: driver.driverName || `Driver ${driver.driverId}`,
            routes: paySheet.routes || [],
            rows: getPaySheetDetails(paySheet)
        })))
            .map(group => {
                const routeLookup = new Map();
                for (const route of group.routes) {
                    const routeId = route?.routeId ?? null;
                    if (routeId == null) {
                        continue;
                    }
                    routeLookup.set(routeId, {
                        location: route.location || '',
                        routeName: route.routeName || ''
                    });
                }

                // Sum individually-rounded line values so the displayed total always
                // equals the visual sum of the rows — avoids sub-cent rounding gaps.
                const displayTotal = group.rows.reduce(
                    (sum, row) => sum + roundMoney(row.routeTotal ?? 0), 0);

                return { ...group, routeLookup, displayTotal };
            })
            .sort((a, b) => a.driverName.localeCompare(b.driverName));
    }, [filteredPaySheetDrivers]);

    const applyPaySheetDriverUpdate = (data) => {
        const updatedDrivers = getPaySheetDrivers(data);
        setPaySheetDrivers(prev => {
            if (prev.length === 0) return updatedDrivers;

            const byDriverId = new Map(prev.map(driver => [`${driver.driverId}`, driver]));
            for (const driver of updatedDrivers) {
                if ((driver.paysheets || []).length === 0) {
                    byDriverId.delete(`${driver.driverId}`);
                } else {
                    byDriverId.set(`${driver.driverId}`, driver);
                }
            }

            return Array.from(byDriverId.values())
                .sort((a, b) => (a.driverName || '').localeCompare(b.driverName || ''));
        });
    };

    // Open the Change-Route modal for a base route line and load the sibling routes for its
    // sub-customer (so the "No Delivery" version can be chosen).
    const openChangeRoute = async (detail) => {
        setChangeRouteDetail(detail);
        setChangeRouteValue("");
        setChangeRouteOptions([]);
        setChangeRouteOpen(true);
        const subId = detail.routeSubCustomerId;
        if (!subId) return;
        setChangeRouteLoading(true);
        try {
            const token = await Session.getToken();
            const res = await fetch(`/api/routes/BySubCustomer/${subId}`, { headers: { Authorization: `Bearer ${token}` } });
            const list = res.ok ? await res.json() : [];
            setChangeRouteOptions((list || []).filter(r => r.id !== detail.routeId)
                .sort((a, b) => (a.name || '').localeCompare(b.name || '')));
        } catch {
            setChangeRouteOptions([]);
        } finally {
            setChangeRouteLoading(false);
        }
    };

    const handleChangeRoute = async () => {
        if (!changeRouteDetail || !changeRouteValue) return;
        setChangeRouteSubmitting(true);
        try {
            const token = await Session.getToken();
            const res = await fetch('/api/paysheets/update-line-route', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ paySheetId: changeRouteDetail.paySheetId, paySheetDetailId: changeRouteDetail.id, routeId: Number(changeRouteValue) })
            });
            if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
            const data = await res.json();
            applyPaySheetDriverUpdate(data);
            setChangeRouteOpen(false);
        } catch (e) {
            setError(e.message || 'Failed to change route.');
        } finally {
            setChangeRouteSubmitting(false);
        }
    };

    const openAddAdjustment = (paySheetId) => {
        setAdjustmentEditing(null);
        setAdjustmentPaySheetId(paySheetId);
        setAdjustmentOpen(true);
    };

    const openEditAdjustment = (detail) => {
        setAdjustmentEditing(detail);
        setAdjustmentPaySheetId(detail.paySheetId);
        setAdjustmentOpen(true);
    };

    const openDelete = (detail) => {
        setEditing(detail);
        setDeleteOpen(true);
    };

    const openSubmit = (detail) => {
        setEditing(detail);
        setSubmitOpen(true);
    };

    const openVoid = (paySheetId) => {
        setVoidPaySheetId(paySheetId);
        setVoidReason("");
        setVoidOpen(true);
    };

    const handleVoid = async () => {
        if (!voidPaySheetId || !voidReason.trim()) return;
        setVoidSubmitting(true);
        try {
            const token = await Session.getToken();
            const res = await fetch(voidApi(voidPaySheetId), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ reason: voidReason.trim() })
            });
            if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
            const data = await res.json();
            applyPaySheetDriverUpdate(data);
            setVoidOpen(false);
        } catch (e) {
            setError(e.message || 'Failed to void pay sheet.');
        } finally {
            setVoidSubmitting(false);
        }
    };

    const openAdd = () => {
        setAddForm(emptyAddForm);
        setAddErrors({});
        setAddOpen(true);
    };

    const validateAdd = () => {
        const v = {};
        if (!addForm.driverId) v.driverId = "Driver is required";
        if (!addForm.endingDate) v.endingDate = "Week-ending date is required";
        if (!addForm.description.trim()) v.description = "Description is required";
        if (!addForm.isComment) {
            const amount = Number(addForm.amount);
            if (!addForm.amount || isNaN(amount) || amount <= 0) v.amount = "Amount must be greater than zero";
            const quantity = Number(addForm.quantity);
            if (!addForm.quantity || isNaN(quantity) || quantity <= 0) v.quantity = "Quantity must be greater than zero";
            if (!addForm.transactionTypeId) v.transactionTypeId = "Transaction type is required";
        }
        setAddErrors(v);
        return Object.keys(v).length === 0;
    };

    const handleAdd = async () => {
        if (!validateAdd()) return;
        setAddSubmitting(true);
        try {
            const token = await Session.getToken();
            const body = {
                driverId: Number(addForm.driverId),
                endingDate: addForm.endingDate,
                description: addForm.description.trim(),
                amount: addForm.isComment ? 0 : Number(addForm.amount),
                quantity: addForm.isComment ? 0 : Number(addForm.quantity),
                transactionTypeId: addForm.isComment || !addForm.transactionTypeId ? null : Number(addForm.transactionTypeId),
                isComment: addForm.isComment,
            };
            const res = await fetch('/api/paysheets/create-standalone', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify(body)
            });
            if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
            const data = await res.json();
            applyPaySheetDriverUpdate(data);
            setAddOpen(false);
        } catch (e) {
            setError(e.message || 'Failed to add pay sheet.');
        } finally {
            setAddSubmitting(false);
        }
    };

    const generate = async () => {
        setLoading(true);
        setError("");
        try {
            const token = await Session.getToken();
            const payWeek = selectedWeek ? dayjs(selectedWeek, 'MM/DD/YYYY').format('YYYY-MM-DD') : dayjs().subtract(7, 'day').format('YYYY-MM-DD');
            const res = await fetch(generateApi(driverId, payWeek), {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` }
            });

            if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
            const data = await res.json();
            if (data.schedule) setSchedule(data.schedule);
            setPaySheetDrivers(getPaySheetDrivers(data));
            setOpenRows(new Set());
            setDetailsCache({});
        } catch (e) {
            setError(e.message || 'Failed to generate pay sheets.');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!editing) return;

        const token = await Session.getToken();
        const res = await fetch(deleteSurchargeApi(editing.paySheetId, editing.id), {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}` }
        });

        if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
        const data = await res.json();
        applyPaySheetDriverUpdate(data);
        setDeleteOpen(false);
    };

    const handleSubmit = async () => {
        if (!editing) return;

        setActionLoading(true);
        try {
            const token = await Session.getToken();
            const res = await fetch(submitApi(editing.paySheetId), {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` }
            });

            if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
            await loadPaySheetData({ showLoading: false });
            setSubmitOpen(false);
        } catch (e) {
            setError(e.message || 'Failed to submit pay sheet.');
        } finally {
            setActionLoading(false);
        }
    };

    const toggleRow = async (detail) => {
        if (!detail || !isBaseRouteRow(detail)) return;

        const rowKey = `${detail.paySheetId}:${detail.id}`;
        const next = new Set(openRows);

        if (next.has(rowKey)) {
            next.delete(rowKey);
            setOpenRows(next);
            return;
        }

        if (!detailsCache[rowKey]) {
            setLoadingDetails(prev => ({ ...prev, [rowKey]: true }));
            try {
                const token = await Session.getToken();
                const res = await fetch(dailyBreakdownApi(detail.paySheetId, detail.id), {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
                const rows = await res.json();
                setDetailsCache(prev => ({ ...prev, [rowKey]: rows }));
            } catch (e) {
                setError(e.message || 'Failed to load route detail.');
                setTimeout(() => setError(""), 4000);
            } finally {
                setLoadingDetails(prev => ({ ...prev, [rowKey]: false }));
            }
        }

        next.add(rowKey);
        setOpenRows(next);
    };

    const hasExisting = paySheetDrivers.some(driver => (driver.paysheets || []).length > 0);

    return (
        <Container className="mt-0">
            <Card>
                <CardBody>
                    <Row className="mb-3">
                        <Col><h5>Pay Sheet Review</h5></Col>
                        <Col className="text-end">
                            <Button size="sm" color="success" onClick={openAdd}>+ Add Paysheet</Button>
                        </Col>
                    </Row>

                    <Row className="mb-3">
                        <Col md={6}>
                            <Input
                                type="select"
                                value={selectedWeek}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setSelectedWeek(value);
                                    saveFinanceFilter("selectedWeek", value);
                                }}
                            >
                                <option value="">-- Select Week --</option>
                                {weeks.map(w => (
                                    <option key={w.endingDate} value={w.endingDate}>{w.displayDate}</option>
                                ))}
                            </Input>
                        </Col>
                        <Col md={6}>
                            <Typeahead
                                id="paysheet-review-driver-filter"
                                labelKey="name"
                                options={drivers}
                                selected={drivers.filter(d => `${d.id}` === `${driverId}`)}
                                onChange={(selected) => setDriverId(selected.length ? selected[0].id : "")}
                                placeholder="-- Select Driver --"
                                clearButton
                            />
                        </Col>
                    </Row>

                    {error && <Alert color="danger">{error}</Alert>}

                    {schedule && (
                        <Row className="mb-4">
                            <Col md={4}>
                                <Card className="shadow-sm">
                                    <CardBody>
                                        <div className="text-muted">Payment Week</div>
                                        <div className="h5 mb-0">{fmt(schedule.startDate)} - {fmt(schedule.endDate)}</div>
                                    </CardBody>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="shadow-sm">
                                    <CardBody>
                                        <div className="text-muted">Pay Sheet Date</div>
                                        <div className="h5 mb-0">{fmt(schedule.payDate)}</div>
                                    </CardBody>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Button
                                    color={hasExisting ? 'warning' : 'primary'}
                                    onClick={generate}
                                    disabled={loading}
                                >
                                    {loading ? 'Working...' : hasExisting ? 'Refresh Pay Sheet Data' : 'Generate Pay Sheet Data'}
                                </Button>
                            </Col>
                        </Row>
                    )}

                    {loading ? (
                        <div className="text-center py-4"><Spinner /></div>
                    ) : (
                        <Table bordered hover responsive>
                            <thead className="table-light">
                                <tr>
                                    <th></th>
                                    <th>Driver Name</th>
                                    <th>Location</th>
                                    <th>Route Name</th>
                                    <th>Description</th>
                                    <th>Qty</th>
                                    <th>Mileage</th>
                                    <th>Hours</th>
                                    <th>Rate</th>
                                    <th>Line Total</th>
                                    <th>Pay Sheet Total</th>
                                    <th></th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {groupedPaySheets.length === 0 ? (
                                    <tr><td colSpan="13" className="text-center">No pay sheets for the selected week.</td></tr>
                                ) : (
                                    groupedPaySheets.map((group) => (
                                        getDisplayRows(group.rows, group.routeLookup).flatMap(({ detail, showDriverName, showRouteInfo }) => {
                                            const routeInfo = getRouteInfo(detail, group.routeLookup);
                                            const isBaseRow = isBaseRouteRow(detail);
                                            const isOvertime = isOvertimeRow(detail);
                                            const isAggregated = detail.surchargeId == null && detail.surchargeTypeId != null;
                                            const isInvoiceSurcharge = isAggregated || (detail.surchargeId != null && !isDriverSurchargeRow(detail));
                                            const description = isOvertime
                                                ? 'Additional drive time'
                                                : (detail.surchargeId != null || isAggregated)
                                                    ? (detail.surchargeDescription || detail.surchargeTypeName || '')
                                                    : (detail.transactionName ?? '');
                                            const rowKey = `${detail.paySheetId}:${detail.id}`;
                                            const isOpen = openRows.has(rowKey);
                                            const isAdjustment = isAdjustmentRow(detail);
                                            const isComment = isCommentRow(detail);
                                            const showDelete = (isDriverSurchargeRow(detail) && !isInvoiceSurcharge) || isAdjustment || isComment;
                                            // In "Edit Rates" mode any line may be removed (a manager may drop a fuel
                                            // surcharge on a no-delivery week, a toll line, an errant route line, etc.).
                                            const canDelete = showDelete || editRatePaySheets.has(group.paySheetId);
                                            // The Line Total is directly editable in "Edit Rates" mode on every
                                            // dollar-carrying generated line (route, drive time, fuel/toll/W&T surcharges,
                                            // false-trip, backup, lead). Comment lines have no amount, and Adjustments are
                                            // edited through their own Edit dialog (amount + sign via transaction type).
                                            const totalEditable = editRatePaySheets.has(group.paySheetId) && !isComment && !isAdjustment;
                                            // Route-less adjustment / driver-surcharge lines have no route to derive a
                                            // QBO Class from, so they get an inline Class (= sub-customer) picker in the
                                            // Location cell. Comment lines are excluded from the check and need none.
                                            const needsClassPicker = !isComment && detail.routeId == null
                                                && (isAdjustment || (isDriverSurchargeRow(detail) && !isInvoiceSurcharge));
                                            // Rate is editable only in this paysheet's "edit rates" mode, and only on route
                                            // and additional-drive-time (overtime) lines.
                                            const rateEditable = editRatePaySheets.has(group.paySheetId) && (isBaseRow || isOvertime);
                                            // Days / Hours / Mileage are editable in the same "edit rates" mode, but only on
                                            // base ROUTE lines (not overtime or any other line type).
                                            const fieldsEditable = editRatePaySheets.has(group.paySheetId) && isBaseRow;
                                            // At-a-glance route notes (paysheet col E + invoicing col F), so a route's
                                            // notes are visible without expanding the daily breakdown.
                                            const routeNotesText = [
                                                detail.routeComments ? `Paysheet notes:\n${detail.routeComments}` : null,
                                                detail.routeInvoicingComments ? `Invoicing notes:\n${detail.routeInvoicingComments}` : null
                                            ].filter(Boolean).join('\n\n');

                                            const mainRow = (
                                                <tr key={detail.id}>
                                                    <td className="text-center">
                                                        {isBaseRow && (
                                                            <Button size="sm" color="link" onClick={() => toggleRow(detail)}>
                                                                {isOpen ? '−' : '+'}
                                                            </Button>
                                                        )}
                                                    </td>
                                                    <td>{showDriverName ? group.driverName : ''}</td>
                                                    <td>
                                                        {needsClassPicker ? (
                                                            <Input
                                                                type="select"
                                                                bsSize="sm"
                                                                style={{ minWidth: 140 }}
                                                                value={detail.assignedSubCustomerId ?? ''}
                                                                title="QuickBooks Class for this line (the sub-customer it belongs to)."
                                                                onChange={(e) => saveLineSubCustomer(detail, e.target.value)}
                                                            >
                                                                <option value="">-- Class --</option>
                                                                {subCustomers.map(s => (
                                                                    <option key={s.id} value={s.id}>
                                                                        {s.name}{!s.qbClassId ? ' (no class)' : ''}
                                                                    </option>
                                                                ))}
                                                            </Input>
                                                        ) : (showRouteInfo ? routeInfo.location : '')}
                                                    </td>
                                                    <td>
                                                        {showRouteInfo ? routeInfo.routeName : ''}
                                                        {showRouteInfo && routeNotesText && (
                                                            <Button size="sm" color="info" outline className="ms-2" title="View route notes" onClick={() => { setNotesText(routeNotesText); setNotesOpen(true); }}>Notes</Button>
                                                        )}
                                                        {showRouteInfo && isBaseRow && editRatePaySheets.has(group.paySheetId) && (
                                                            <Button size="sm" color="secondary" outline className="ms-2" title="Reassign this route (e.g. to its No Delivery version)" onClick={() => openChangeRoute(detail)}>Change</Button>
                                                        )}
                                                    </td>
                                                    <td>{isFalseTripRow(detail) ? <Badge color="warning">False Trip</Badge> : isBackupDriverRow(detail) ? <Badge color="info">Backup Driver</Badge> : isBackupMakeupRow(detail) ? <Badge color="info">Backup Driver</Badge> : isLeadDriverRow(detail) ? <Badge color="primary">Lead Pay</Badge> : isAdjustmentRow(detail) ? <span><Badge color="secondary">Adjustment</Badge> {detail.description}</span> : isCommentRow(detail) ? <span><Badge color="secondary">Comment</Badge> {detail.description}</span> : description}</td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        {(isBackupDriverRow(detail) || isLeadDriverRow(detail)) ? (
                                                            <Input
                                                                type="number"
                                                                bsSize="sm"
                                                                min="0"
                                                                step="1"
                                                                style={{ width: 72, display: 'inline-block' }}
                                                                defaultValue={isBackupDriverRow(detail) ? (detail.daysOnRoute ?? 0) : (detail.guaranteeDays ?? 5)}
                                                                title={isBackupDriverRow(detail) ? "Standby days paid at the base rate (days × rate). Auto-set to guarantee − days covered; edit to override." : "Guaranteed management days paid at the lead rate."}
                                                                onBlur={(e) => saveGuaranteeDays(detail, e.target.value)}
                                                                onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                                            />
                                                        ) : fieldsEditable ? (
                                                            <Input
                                                                type="number"
                                                                bsSize="sm"
                                                                min="0"
                                                                step="any"
                                                                style={{ width: 80, display: 'inline-block' }}
                                                                defaultValue={detail.daysOnRouteOverride ?? detail.daysOnRoute ?? ""}
                                                                title={detail.daysOnRouteOverride != null ? "Overridden days-on-route for this paysheet. Clear the field to revert to the scheduled value." : "Temporary days-on-route override for this paysheet. Clear the field to revert to the scheduled value."}
                                                                onBlur={(e) => saveDaysOnRoute(detail, e.target.value)}
                                                                onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                                            />
                                                        ) : rowQuantity(detail, isBaseRow, isOvertime, hours)}
                                                    </td>
                                                    <td style={{ textAlign: 'right' }}>
                                                        {fieldsEditable ? (
                                                            <Input
                                                                type="number"
                                                                bsSize="sm"
                                                                min="0"
                                                                step="any"
                                                                style={{ width: 90, display: 'inline-block' }}
                                                                defaultValue={detail.totalMileageOverride ?? detail.totalMileage ?? ""}
                                                                title={detail.totalMileageOverride != null ? "Overridden mileage for this paysheet. Clear the field to revert to the scheduled value." : "Temporary mileage override for this paysheet. Clear the field to revert to the scheduled value."}
                                                                onBlur={(e) => saveMileage(detail, e.target.value)}
                                                                onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                                            />
                                                        ) : (detail.totalMileageOverride ?? detail.totalMileage ?? 0)}
                                                    </td>
                                                    <td style={{ textAlign: 'right' }}>
                                                        {fieldsEditable ? (
                                                            <Input
                                                                type="number"
                                                                bsSize="sm"
                                                                min="0"
                                                                step="any"
                                                                style={{ width: 90, display: 'inline-block' }}
                                                                defaultValue={detail.totalHoursWorkedOverride ?? detail.totalHoursWorked ?? ""}
                                                                title={detail.totalHoursWorkedOverride != null ? "Overridden hours for this paysheet. Clear the field to revert to the scheduled value." : "Temporary hours override for this paysheet. Clear the field to revert to the scheduled value."}
                                                                onBlur={(e) => saveHours(detail, e.target.value)}
                                                                onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                                            />
                                                        ) : (isSurchargeRow(detail) ? '' : hours(detail.totalHoursWorkedOverride ?? detail.totalHoursWorked))}
                                                    </td>
                                                    <td style={{ textAlign: 'right' }}>
                                                        {rateEditable ? (
                                                            <Input
                                                                type="number"
                                                                bsSize="sm"
                                                                min="0"
                                                                step="0.01"
                                                                style={{ width: 90, display: 'inline-block' }}
                                                                defaultValue={detail.payRate ?? 0}
                                                                title={detail.payRateOverride != null ? "Overridden rate for this paysheet only. Clear the field to revert to the route's rate." : "Temporary rate for this paysheet only. Clear the field to revert to the route's rate."}
                                                                onBlur={(e) => saveRate(detail, e.target.value)}
                                                                onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                                            />
                                                        ) : (
                                                            <span title={detail.payRateOverride != null ? "Rate overridden for this paysheet" : undefined} style={detail.payRateOverride != null ? { fontWeight: 'bold' } : undefined}>${rateMoney(detail)}</span>
                                                        )}
                                                    </td>
                                                    <td style={{ textAlign: 'right' }}>
                                                        {totalEditable ? (
                                                            <Input
                                                                type="number"
                                                                bsSize="sm"
                                                                step="0.01"
                                                                style={{ width: 100, display: 'inline-block' }}
                                                                defaultValue={detail.routeTotal ?? 0}
                                                                title={detail.routeTotalOverride != null ? "Overridden line total for this paysheet. Clear the field to revert to the computed total." : "Temporary line-total override for this paysheet. Clear the field to revert to the computed total."}
                                                                onBlur={(e) => saveLineTotal(detail, e.target.value)}
                                                                onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                                            />
                                                        ) : (
                                                            <span title={detail.routeTotalOverride != null ? "Line total overridden for this paysheet" : undefined} style={detail.routeTotalOverride != null ? { fontWeight: 'bold' } : undefined}>${money(detail.routeTotal)}</span>
                                                        )}
                                                    </td>
                                                    <td style={{ textAlign: 'right' }}>{showDriverName ? `$${money(group.displayTotal)}` : ''}</td>
                                                    <td className="text-center">
                                                        {showDriverName && (
                                                            <Button size="sm" color="success" title="Add a one-off pay line" onClick={() => openAddAdjustment(detail.paySheetId)}>+ Adj</Button>
                                                        )}
                                                        {(isAdjustment || isComment) && (
                                                            <Button size="sm" color="info" style={{ marginLeft: 5 }} onClick={() => openEditAdjustment(detail)}>Edit</Button>
                                                        )}
                                                        {canDelete && (
                                                            <Button size="sm" color="warning" style={{ marginLeft: 5 }} title="Remove this line" onClick={() => openDelete(detail)}>-</Button>
                                                        )}
                                                    </td>
                                                    <td className="text-center">
                                                        {showDriverName && (
                                                            <>
                                                                <Button size="sm" outline color={editRatePaySheets.has(group.paySheetId) ? "secondary" : "primary"} style={{ marginRight: 5 }} title="Temporarily edit route / drive-time pay rates on this paysheet" onClick={() => toggleEditRate(group.paySheetId)}>
                                                                    {editRatePaySheets.has(group.paySheetId) ? "Done" : "Edit Rates"}
                                                                </Button>
                                                                <Button size="sm" color="primary" onClick={() => openSubmit(detail)}>Submit</Button>
                                                                <Button size="sm" color="danger" style={{ marginLeft: 5 }} title="Void this pay sheet (false paycheck)" onClick={() => openVoid(detail.paySheetId)}>Void</Button>
                                                            </>
                                                        )}
                                                    </td>
                                                </tr>
                                            );

                                            const detailRow = isBaseRow ? (
                                                <tr key={`${detail.id}-detail`}>
                                                    <td colSpan="13" className="p-0 border-top-0">
                                                        <Collapse isOpen={isOpen}>
                                                            <div className="p-3 bg-light">
                                                                {loadingDetails[rowKey] ? (
                                                                    <div className="text-center py-2"><Spinner size="sm" /></div>
                                                                ) : (
                                                                    <Table size="sm" bordered responsive className="mb-0">
                                                                        <thead>
                                                                            <tr>
                                                                                <th>Day</th>
                                                                                <th>Start Time</th>
                                                                                <th>End Time</th>
                                                                                <th>Hours Worked</th>
                                                                                {WT_ADT_ENABLED && <th title="Additional Drive Time">ADT</th>}
                                                                                <th>Mileage</th>
                                                                                <th>Days on Route</th>
                                                                                <th style={{ width: 80 }}>Notes</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {(detailsCache[rowKey] || []).length === 0 ? (
                                                                                <tr><td colSpan={WT_ADT_ENABLED ? 8 : 7} className="text-center">No daily route detail found.</td></tr>
                                                                            ) : (
                                                                                (detailsCache[rowKey] || []).map((item, itemIndex) => (
                                                                                    <tr key={`${rowKey}-${itemIndex}`}>
                                                                                        <td>{fmt(item.date)}</td>
                                                                                        <td>{formatAmPm(item.startTime)}</td>
                                                                                        <td>{formatAmPm(item.endTime)}</td>
                                                                                        <td style={{ textAlign: 'right' }}>{hours(item.hoursWorked)}</td>
                                                                                        {WT_ADT_ENABLED && <td style={{ textAlign: 'center' }}>{calcDailyOt(item.startTime, item.endTime, detail)}</td>}
                                                                                        <td style={{ textAlign: 'right' }}>{item.mileage ?? 0}</td>
                                                                                        <td style={{ textAlign: 'center' }}>{hours(item.daysOnRoute)}</td>
                                                                                        <td className="text-center">
                                                                                            {item.comments ? (
                                                                                                <Button
                                                                                                    size="sm"
                                                                                                    color="info"
                                                                                                    title={item.comments}
                                                                                                    onClick={() => { setNotesText(item.comments); setNotesOpen(true); }}
                                                                                                >
                                                                                                    Notes
                                                                                                </Button>
                                                                                            ) : (
                                                                                                <span className="text-muted">—</span>
                                                                                            )}
                                                                                        </td>
                                                                                    </tr>
                                                                                ))
                                                                            )}
                                                                        </tbody>
                                                                    </Table>
                                                                )}
                                                            </div>
                                                        </Collapse>
                                                    </td>
                                                </tr>
                                            ) : null;

                                            return detailRow ? [mainRow, detailRow] : [mainRow];
                                        })
                                    ))
                                )}
                            </tbody>
                        </Table>
                    )}
                </CardBody>
            </Card>

            <Modal isOpen={adjustmentOpen} toggle={() => setAdjustmentOpen(false)} size="lg">
                <ModalHeader toggle={() => setAdjustmentOpen(false)}>{adjustmentEditing ? "Edit Adjustment" : "Add Adjustment"}</ModalHeader>
                <ModalBody>
                    <PaySheetAdjustmentForm
                        paySheetId={adjustmentPaySheetId}
                        adjustment={adjustmentEditing}
                        onSuccess={(data) => { applyPaySheetDriverUpdate(data); setAdjustmentOpen(false); }}
                        onCancel={() => { setAdjustmentOpen(false); }}
                    />
                </ModalBody>
            </Modal>

            <Modal isOpen={deleteOpen} toggle={() => setDeleteOpen(false)}>
                <ModalHeader toggle={() => setDeleteOpen(false)}>Remove Line</ModalHeader>
                <ModalBody>
                    <FormGroup>
                        <Label>This will remove the selected line, do you want to continue?</Label>
                    </FormGroup>
                    <div className="d-flex justify-content-end gap-2">
                        <Button color="secondary" onClick={() => setDeleteOpen(false)}>Cancel</Button>
                        <Button color="warning" onClick={handleDelete}>Remove</Button>
                    </div>
                </ModalBody>
            </Modal>

            <Modal isOpen={notesOpen} toggle={() => setNotesOpen(false)}>
                <ModalHeader toggle={() => setNotesOpen(false)}>Notes</ModalHeader>
                <ModalBody>
                    <div style={{ whiteSpace: 'pre-wrap' }}>{notesText || "(no notes)"}</div>
                    <div className="d-flex justify-content-end gap-2 mt-3">
                        <Button color="secondary" onClick={() => setNotesOpen(false)}>Close</Button>
                    </div>
                </ModalBody>
            </Modal>

            <Modal isOpen={changeRouteOpen} toggle={() => !changeRouteSubmitting && setChangeRouteOpen(false)}>
                <ModalHeader toggle={() => !changeRouteSubmitting && setChangeRouteOpen(false)}>Change Route</ModalHeader>
                <ModalBody>
                    <FormGroup>
                        <Label>Reassign <strong>{changeRouteDetail?.routeName}</strong> to:</Label>
                        {changeRouteLoading ? (
                            <div className="text-center py-2"><Spinner size="sm" /></div>
                        ) : (
                            <Input type="select" value={changeRouteValue} onChange={(e) => setChangeRouteValue(e.target.value)}>
                                <option value="">-- Select Route --</option>
                                {changeRouteOptions.map(r => (
                                    <option key={r.id} value={r.id}>{r.name}</option>
                                ))}
                            </Input>
                        )}
                    </FormGroup>
                    <div className="text-muted small mb-2">
                        Switching to a “No Delivery” route removes its fuel surcharge and pays the flat no-delivery rate.
                        Regenerate the linked invoice afterward so it matches.
                    </div>
                    <div className="d-flex justify-content-end gap-2">
                        <Button color="secondary" onClick={() => setChangeRouteOpen(false)} disabled={changeRouteSubmitting}>Cancel</Button>
                        <Button color="primary" onClick={handleChangeRoute} disabled={changeRouteSubmitting || !changeRouteValue}>
                            {changeRouteSubmitting ? <><Spinner size="sm" /> Changing…</> : 'Change Route'}
                        </Button>
                    </div>
                </ModalBody>
            </Modal>

            <Modal isOpen={submitOpen} toggle={() => setSubmitOpen(false)}>
                <ModalHeader toggle={() => setSubmitOpen(false)}>Submit Pay Sheet</ModalHeader>
                <ModalBody>
                    <FormGroup>
                        <Label>This will submit the selected pay sheet for approval, do you want to continue?</Label>
                    </FormGroup>
                    <div className="d-flex justify-content-end gap-2">
                        <Button color="secondary" onClick={() => setSubmitOpen(false)} disabled={actionLoading}>Cancel</Button>
                        <Button color="primary" onClick={handleSubmit} disabled={actionLoading}>
                            {actionLoading ? <><Spinner size="sm" /> Submitting...</> : 'Submit'}
                        </Button>
                    </div>
                </ModalBody>
            </Modal>

            <Modal isOpen={voidOpen} toggle={() => setVoidOpen(false)}>
                <ModalHeader toggle={() => setVoidOpen(false)}>Void Pay Sheet</ModalHeader>
                <ModalBody>
                    <FormGroup>
                        <Label>This will void the selected pay sheet (false paycheck). Please provide a reason.</Label>
                        <Input
                            type="textarea"
                            rows={3}
                            value={voidReason}
                            onChange={(e) => setVoidReason(e.target.value)}
                            placeholder="Reason for voiding this pay sheet"
                        />
                    </FormGroup>
                    <div className="d-flex justify-content-end gap-2">
                        <Button color="secondary" onClick={() => setVoidOpen(false)} disabled={voidSubmitting}>Cancel</Button>
                        <Button color="danger" onClick={handleVoid} disabled={voidSubmitting || !voidReason.trim()}>
                            {voidSubmitting ? <><Spinner size="sm" /> Voiding...</> : 'Void'}
                        </Button>
                    </div>
                </ModalBody>
            </Modal>

            <Modal isOpen={addOpen} toggle={() => setAddOpen(false)} size="lg">
                <ModalHeader toggle={() => setAddOpen(false)}>Add Pay Sheet</ModalHeader>
                <ModalBody>
                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label>Driver</Label>
                                <Input
                                    type="select"
                                    value={addForm.driverId}
                                    onChange={(e) => setAddForm(p => ({ ...p, driverId: e.target.value }))}
                                    invalid={!!addErrors.driverId}
                                >
                                    <option value="">-- Select Driver --</option>
                                    {allDrivers.map(d => (
                                        <option key={d.id} value={d.id}>{d.driverName}</option>
                                    ))}
                                </Input>
                                {addErrors.driverId && <div className="text-danger small">{addErrors.driverId}</div>}
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label>Week Ending</Label>
                                <Input
                                    type="date"
                                    value={addForm.endingDate}
                                    onChange={(e) => setAddForm(p => ({ ...p, endingDate: e.target.value }))}
                                    invalid={!!addErrors.endingDate}
                                />
                                {addErrors.endingDate && <div className="text-danger small">{addErrors.endingDate}</div>}
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={12}>
                            <FormGroup>
                                <Label>Description</Label>
                                <Input
                                    type="text"
                                    maxLength={500}
                                    value={addForm.description}
                                    onChange={(e) => setAddForm(p => ({ ...p, description: e.target.value }))}
                                    invalid={!!addErrors.description}
                                />
                                {addErrors.description && <div className="text-danger small">{addErrors.description}</div>}
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={12}>
                            <FormGroup check className="mb-3">
                                <Input
                                    id="paysheet-add-is-comment"
                                    type="checkbox"
                                    checked={addForm.isComment}
                                    onChange={(e) => setAddForm(p => ({ ...p, isComment: e.target.checked }))}
                                />
                                <Label check for="paysheet-add-is-comment">Comment only (no amount)</Label>
                            </FormGroup>
                        </Col>
                    </Row>
                    {!addForm.isComment && (
                        <Row>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Quantity</Label>
                                    <Input
                                        type="number"
                                        step="any"
                                        min="0"
                                        value={addForm.quantity}
                                        onChange={(e) => setAddForm(p => ({ ...p, quantity: e.target.value }))}
                                        invalid={!!addErrors.quantity}
                                    />
                                    {addErrors.quantity && <div className="text-danger small">{addErrors.quantity}</div>}
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Amount</Label>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={addForm.amount}
                                        onChange={(e) => setAddForm(p => ({ ...p, amount: e.target.value }))}
                                        invalid={!!addErrors.amount}
                                    />
                                    {addErrors.amount && <div className="text-danger small">{addErrors.amount}</div>}
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Transaction Type</Label>
                                    <Input
                                        type="select"
                                        value={addForm.transactionTypeId}
                                        onChange={(e) => setAddForm(p => ({ ...p, transactionTypeId: e.target.value }))}
                                        invalid={!!addErrors.transactionTypeId}
                                    >
                                        <option value="">-- Transaction Type --</option>
                                        {addTransactionTypes.map(t => (
                                            <option key={t.id} value={t.id}>
                                                {t.transactionName}{t.isPositiveAdjustment === false ? " (deduction)" : ""}
                                            </option>
                                        ))}
                                    </Input>
                                    {addErrors.transactionTypeId && <div className="text-danger small">{addErrors.transactionTypeId}</div>}
                                </FormGroup>
                            </Col>
                        </Row>
                    )}
                    <div className="d-flex justify-content-end gap-2">
                        <Button color="secondary" onClick={() => setAddOpen(false)} disabled={addSubmitting}>Cancel</Button>
                        <Button color="primary" onClick={handleAdd} disabled={addSubmitting}>
                            {addSubmitting ? <><Spinner size="sm" /> Saving...</> : 'Add Pay Sheet'}
                        </Button>
                    </div>
                </ModalBody>
            </Modal>
        </Container>
    );
};

export default PaySheetReview;
